import React, { ChangeEventHandler, CSSProperties } from "react";

import {
  FormControl,
  FormHelperText,
  Select as SelectMI,
  MenuItem,
  Tooltip,
  OutlinedSelectProps,
  BaseSelectProps,
} from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import classnames from "classnames";
import { FiInfo, FiChevronDown } from "react-icons/fi";

import { getMuiPopupContainer } from "../../../container/themes/dispatchTheme";
import { Error } from "../../../models/error";
import useStyles from "./styles";
import { getBrandCustom } from "Util/getCustomColor";
import { colors } from "@zapalacorp/tokens";

type SelectProps = OutlinedSelectProps & BaseSelectProps;

export type Props = SelectProps & {
  name?: string;
  label?: string;
  error?: boolean;
  errorMessage?: string;
  errorResponse?: Error;
  defaultValue?: unknown;
  value?: string | ReadonlyArray<string> | number | Array<number> | undefined;
  children?: any;
  register?: any;
  style?: CSSProperties;
  labelStyle?: CSSProperties;
  dataCy?: string;
  bordercolor?: string;
  infoMessage?: React.ReactNode | string;
  emptyPlaceholder?: string;
};

const FormControlCustomized = withStyles(() => ({
  root: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    borderColor: "#D0D5DD",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
    "&:active .MuiOutlinedInput-notchedOutline": {
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
    "&:focus .MuiOutlinedInput-notchedOutline": {
      backgroundColor: "transparent",
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
    "& .MuiSelect-select:focus": {
      backgroundColor: "transparent !important",
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
  },
}))(FormControl);

const SelectIcon = styled(FiChevronDown)(() => ({
  marginRight: "5px",
  width: "20px",
  height: "20px",
}));

const Select = (props: Props) => {
  const {
    name,
    label,
    labelStyle,
    errorMessage,
    errorResponse,
    disabled,
    error,
    children,
    defaultValue,
    value,
    className,
    onChange,
    register,
    placeholder,
    emptyPlaceholder,
    displayEmpty = true,
    dataCy,
    required,
    infoMessage,
    ...rest
  } = props;

  const classes = useStyles({ disabled });

  const renderInfoTooltip = () => {
    if (!infoMessage) return null;

    return (
      <Tooltip
        title={<>{infoMessage}</>}
        placement="top"
        arrow
      >
        <div className={classes.tooltip}>
          <FiInfo />
        </div>
      </Tooltip>
    );
  };

  const renderRequiredLabel = () => {
    if (required) {
      return <span className={classes.required}>{"*"}</span>;
    }

    return null;
  };

  const renderLabel = () => {
    if (!label) return null;

    return (
      <span className={classes.label} style={labelStyle}>
        {label}
        {renderRequiredLabel()}
        {renderInfoTooltip()}
      </span>
    );
  };

  const renderError = () => {
    if (error && errorMessage) {
      return (
        <FormHelperText className={classes.formHelper} data-cy="errors">
          <InfoOutlinedIcon htmlColor="#D92D20" className={classes.iconError} />
          {errorMessage}
        </FormHelperText>
      );
    }

    if (errorResponse && errorResponse?.field === name) {
      return (
        <FormHelperText className={classes.formHelper} data-cy="errors">
          <InfoOutlinedIcon htmlColor="#D92D20" className={classes.iconError} />
          {errorResponse?.message}
        </FormHelperText>
      );
    }

    return null;
  };

  const renderPlaceholder = () => {
    const hasChildren = Array.isArray(children)
      ? !!children.length
      : children !== null && children !== undefined && children !== "";

    if (!hasChildren && !!emptyPlaceholder) {
      return (
        <MenuItem className={classes.menuItemPlaceholder} data-cy="placeholder" disabled value="">
          <span className={classnames(classes.text, classes.placeholder)}>{emptyPlaceholder}</span>
        </MenuItem>
      );
    }

    if (placeholder) {
      return (
        <MenuItem className={classes.menuItemPlaceholder} data-cy="placeholder" disabled value="">
          <span className={classnames(classes.text, classes.placeholder)}>{placeholder}</span>
        </MenuItem>
      );
    }

    return null;
  };

  return (
    <FormControlCustomized
      disabled={disabled}
      bordercolor={error ? "#D92D20" : getBrandCustom("primaryColor", colors.brandBlue500)}
      fullWidth
      data-cy={`form-control-${dataCy}`}
      variant="outlined"
    >
      {renderLabel()}

      <SelectMI
        tabIndex={0}
        fullWidth
        data-cy={dataCy}
        onChange={onChange}
        name={name}
        defaultValue={defaultValue || ""}
        value={value || ""}
        error={error}
        ref={register}
        disabled={disabled}
        SelectDisplayProps={{ tabIndex: 0 }}
        MenuProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          transformOrigin: { horizontal: "center", vertical: "top" },
          classes: {
            paper: classes.menuItem,
          },
          PaperProps: {
            style: {
              borderRadius: "8px",
            },
          },
          container: getMuiPopupContainer,
        }}
        IconComponent={SelectIcon}
        classes={{
          icon: classes.selectIcon,
        }}
        className={classnames(classes.select, classes.text, className)}
        displayEmpty={displayEmpty}
        {...rest}
      >
        {renderPlaceholder()}
        {children}
      </SelectMI>

      {renderError()}
    </FormControlCustomized>
  );
};

export default Select;